import React from 'react';
import {
  styled,
  useTheme,
  TextCustom,
  TextTag,
  css,
} from '@vw-marketing/us-components';
import type { TextCustomProps } from '@vw-marketing/us-components';
import {
  applyCustomStyles,
  applyCustomStylesToChildren,
  separateNestedSelectors,
  applyNestedSelectors,
} from '../utils';

interface TextBoxProps extends TextCustomProps {
  customStyle?: React.CSSProperties | ((theme: any) => React.CSSProperties);
  wrapperStyle?: React.CSSProperties | ((theme: any) => React.CSSProperties);
  className?: string;
  component?: keyof JSX.IntrinsicElements;
  [key: string]: any;
}

const StyledWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) =>
    !['$customStyles', '$wrapperStyles'].includes(prop),
})<{ $customStyles?: React.CSSProperties }>`
  ${({ $wrapperStyles }) => {
    if (!$wrapperStyles || typeof $wrapperStyles !== 'object') return '';

    const { nestedSelectors, regularStyles } =
      separateNestedSelectors($wrapperStyles);

    return css`
      ${applyCustomStyles(regularStyles as React.CSSProperties)}
      ${applyNestedSelectors(nestedSelectors)}
    `;
  }}
  ${({ $customStyles }) => applyCustomStylesToChildren($customStyles)}
`;

const INLINE_TAGS = ['span', 'strong', 'em', 'b', 'i', 'u', 'small'];

export const TextBox = React.forwardRef<HTMLDivElement, TextBoxProps>(
  ({ children, customStyle, wrapperStyle, tag = TextTag.p, ...rest }, ref) => {
    const theme = useTheme();
    const resolvedWrapperStyle =
      typeof wrapperStyle === 'function' ? wrapperStyle(theme) : wrapperStyle;
    const resolvedChildStyle =
      typeof customStyle === 'function' ? customStyle(theme) : customStyle;

    const isInlineTag = INLINE_TAGS.includes(tag);
    const outerTag = isInlineTag ? TextTag.span : tag;

    return (
      <StyledWrapper
        as={outerTag}
        ref={ref}
        $wrapperStyles={resolvedWrapperStyle}
        $customStyles={resolvedChildStyle}
      >
        <TextCustom tag={isInlineTag ? tag : TextTag.span} {...rest}>
          {children}
        </TextCustom>
      </StyledWrapper>
    );
  }
);

TextBox.displayName = 'TextBox';
